import { useMemo, useState } from 'react';
import { FACE_SHAPES } from '../data/shapeContent';
import { copyAnalysisSummary, downloadAnalysisReport } from '../lib/report';
import type { PreparedPhoto } from '../lib/imageUtils';
import type { AdviceCategory, FaceAnalysis } from '../types';
import {
  AlertIcon,
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  InfoIcon,
  RefreshIcon,
  SparkIcon,
} from './Icons';
import { ShapeGlyph } from './ShapeGlyph';

interface ResultViewProps {
  analysis: FaceAnalysis;
  photo: PreparedPhoto | null;
  onReset: () => void;
}

const ADVICE_TABS: Array<{ id: AdviceCategory; label: string }> = [
  { id: 'hair', label: 'Hair' },
  { id: 'glasses', label: 'Glasses' },
  { id: 'beard', label: 'Beard' },
  { id: 'makeup', label: 'Makeup' },
];

function characterLabel(analysis: FaceAnalysis): string {
  switch (analysis.character) {
    case 'strong':
      return 'Clear leading match';
    case 'blended':
      return 'Blended result';
    case 'close':
      return 'Closest geometry match';
  }
}

function matchWord(value: number, leader: number): string {
  const gap = leader - value;
  if (gap <= 2) return 'Nearly tied';
  if (gap <= 7) return 'Very close';
  if (gap <= 14) return 'Possible';
  return 'Less similar';
}

function ratio(value: number): string {
  return value.toFixed(2);
}

export function ResultView({ analysis, photo, onReset }: ResultViewProps): React.ReactElement {
  const [activeAdvice, setActiveAdvice] = useState<AdviceCategory>('hair');
  const [actionStatus, setActionStatus] = useState<string>('');
  const content = FACE_SHAPES[analysis.primary.id];
  const closeMatches = analysis.matches.slice(0, 4);
  const advice = content.advice[activeAdvice];
  const qualityWarnings = analysis.quality.filter((note) => note.level === 'warning');

  const resultDescriptor = useMemo(() => {
    if (analysis.character === 'blended') {
      const second = analysis.matches[1];
      return second
        ? `Your proportions sit between ${content.label} and ${FACE_SHAPES[second.id].label}. The first label is the closest of seven profiles, not a claim that every feature fits one category.`
        : content.summary;
    }
    return content.summary;
  }, [analysis.character, analysis.matches, content]);

  const copyResult = async () => {
    try {
      await copyAnalysisSummary(analysis);
      setActionStatus('Result summary copied.');
    } catch {
      setActionStatus('Copying is blocked in this browser. The downloadable report is still available.');
    }
  };

  const downloadResult = async () => {
    setActionStatus('Creating your private PNG report…');
    try {
      await downloadAnalysisReport(analysis, photo);
      setActionStatus('Private PNG report downloaded.');
    } catch {
      setActionStatus('The report could not be created in this browser.');
    }
  };

  return (
    <section className="result-view" aria-labelledby="result-heading" data-testid="analysis-result">
      <div className="result-view__masthead">
        <div className="result-view__glyph">
          <ShapeGlyph shape={analysis.primary.id} measured label={`${content.label} result diagram`} />
        </div>
        <div className="result-view__headline">
          <span className="eyebrow">
            <SparkIcon /> {characterLabel(analysis)}
          </span>
          <h2 id="result-heading">{content.label}</h2>
          <p className="result-view__summary">{resultDescriptor}</p>
          <p className="result-view__explanation">{analysis.explanation}</p>
        </div>
      </div>

      {qualityWarnings.length > 0 ? (
        <div className="quality-notes" aria-label="Photo quality notes">
          {qualityWarnings.map((note) => (
            <div className="callout callout--warning" key={note.code}>
              <AlertIcon />
              <p>{note.message}</p>
            </div>
          ))}
        </div>
      ) : analysis.source === 'photo' ? (
        <div className="callout callout--success">
          <CheckIcon />
          <p>The face outline was complete enough to compare all four visible proportions.</p>
        </div>
      ) : null}

      <div className="result-grid">
        <section className="match-panel" aria-labelledby="matches-heading">
          <div className="section-label-row">
            <h3 id="matches-heading">How the profiles compare</h3>
            <span>Relative fit, not accuracy</span>
          </div>
          <ol className="match-list">
            {closeMatches.map((match, index) => (
              <li key={match.id}>
                <span className="match-list__rank">0{index + 1}</span>
                <div className="match-list__body">
                  <div className="match-list__label">
                    <strong>{FACE_SHAPES[match.id].label}</strong>
                    <span>{index === 0 ? 'Closest' : matchWord(match.strength, analysis.primary.strength)}</span>
                  </div>
                  <progress
                    max={analysis.primary.strength}
                    value={match.strength}
                    aria-label={`${FACE_SHAPES[match.id].label} relative geometry fit`}
                  />
                </div>
              </li>
            ))}
          </ol>
          <div className="method-note">
            <InfoIcon />
            <p>
              These bars rank your measurements against seven styling profiles. They are neither probabilities nor a scientifically validated accuracy score; small differences usually mean a mixed shape.
            </p>
          </div>
        </section>

        <section className="metric-panel" aria-labelledby="metrics-heading">
          <div className="section-label-row">
            <h3 id="metrics-heading">Your measured geometry</h3>
            <span>{analysis.source === 'photo' ? 'Visible outline' : 'Manual ratios'}</span>
          </div>
          <dl className="metric-list">
            <div>
              <dt>Length ÷ widest point</dt>
              <dd>{ratio(analysis.metrics.lengthToWidth)} : 1</dd>
            </div>
            <div>
              <dt>Forehead ÷ widest point</dt>
              <dd>{ratio(analysis.metrics.foreheadRatio)}</dd>
            </div>
            <div>
              <dt>Cheekbones ÷ widest point</dt>
              <dd>{ratio(analysis.metrics.cheekRatio)}</dd>
            </div>
            <div>
              <dt>Jaw ÷ widest point</dt>
              <dd>{ratio(analysis.metrics.jawRatio)}</dd>
            </div>
            <div>
              <dt>Forehead ÷ jaw</dt>
              <dd>{ratio(analysis.metrics.foreheadToJaw)} : 1</dd>
            </div>
            <div>
              <dt>Approx. jaw corner</dt>
              <dd>{Math.round(analysis.metrics.jawAngle)}°</dd>
            </div>
          </dl>
        </section>
      </div>

      <section className="why-panel" aria-labelledby="why-heading">
        <div className="section-label-row">
          <h3 id="why-heading">Why this result leads</h3>
          <span>{content.signature}</span>
        </div>
        <ol>
          {analysis.reasons.map((reason, index) => (
            <li key={reason}>
              <span>0{index + 1}</span>
              <p>{reason}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="advice-panel" aria-labelledby="advice-heading">
        <div className="advice-panel__heading">
          <div>
            <span className="eyebrow">Work with the proportions</span>
            <h3 id="advice-heading">Styling ideas for a {content.label.toLowerCase()} result</h3>
          </div>
          <div className="advice-tabs" role="tablist" aria-label="Styling guidance category">
            {ADVICE_TABS.map((tab) => (
              <button
                id={`advice-${tab.id}-tab`}
                type="button"
                role="tab"
                aria-selected={activeAdvice === tab.id}
                aria-controls={`advice-${tab.id}-panel`}
                tabIndex={activeAdvice === tab.id ? 0 : -1}
                key={tab.id}
                onClick={() => setActiveAdvice(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div
          id={`advice-${activeAdvice}-panel`}
          className="advice-list"
          role="tabpanel"
          aria-labelledby={`advice-${activeAdvice}-tab`}
        >
          {advice.map((item, index) => (
            <article key={item.title}>
              <span className="advice-list__index">0{index + 1}</span>
              <div>
                <h4>{item.title}</h4>
                <p>{item.why}</p>
                <small>
                  <strong>Use carefully:</strong> {item.useCarefully}
                </small>
              </div>
            </article>
          ))}
        </div>
        <p className="advice-panel__note">
          These are visual-balance ideas, not rules about how anyone should look. Fit, comfort, hair texture, personal expression, and professional advice can matter more than face-shape labels.
        </p>
      </section>

      <div className="result-actions">
        <button className="button button--primary" type="button" onClick={() => void downloadResult()}>
          <DownloadIcon />
          Download private report
        </button>
        <button className="button button--secondary" type="button" onClick={() => void copyResult()}>
          <CopyIcon />
          Copy summary
        </button>
        <button className="button button--quiet" type="button" onClick={onReset}>
          <RefreshIcon />
          Start again
        </button>
      </div>
      <p className="action-status" role="status" aria-live="polite">
        {actionStatus}
      </p>

      <div className="result-disclaimer">
        <InfoIcon />
        <p>
          Face-shape labels are simplified styling heuristics based on a visible 2D outline. They do not determine identity, attractiveness, health, age, or any protected trait.
        </p>
      </div>
    </section>
  );
}
